import { type Ref, onUnmounted, ref, unref, watch } from 'vue'
import {
  fetchMatrixMediaBlobUrl,
  invalidateMatrixMediaBlobUrl,
  isMatrixMediaHttpUrl,
  peekMatrixMediaBlobUrl,
  releaseMatrixMediaBlobUrl,
  rewriteMatrixMediaHttpUrl,
  toAuthenticatedMatrixMediaUrl,
  toAvatarMatrixMediaUrl,
  toPosterMatrixMediaUrl,
} from '@/modules/chat-matrix-fast/services/matrixMediaUrl'
import { getMatrixClient } from '@/modules/chat-matrix-fast/services/matrixClientLifecycle'
import { isSameAvatarAsset } from '@/modules/chat-matrix-fast/utils/userDataExtractor'
import {
  findWarmDirectAvatar,
  rememberWarmDirectAvatar,
} from '@/modules/chat-matrix-fast/utils/avatarWarmPaint'
import {
  avatarDebugLog,
  summarizeAvatarUrlForLog,
} from '@/modules/chat-matrix-fast/utils/chatDebugLog'

export type AuthenticatedMediaPurpose = 'avatar' | 'attachment' | 'poster'

export interface UseAuthenticatedMediaUrlOptions {
  /**
   * `avatar` — force Matrix thumbnail (96×96); never rewrite attachments.
   * `attachment` — full `/media/download` (message images/videos/gallery).
   * `poster` — Matrix `/thumbnail/` for video preview; never full-download fallback.
   */
  purpose?: AuthenticatedMediaPurpose
}

function allowsFullDownloadFallback(purpose: AuthenticatedMediaPurpose): boolean {
  return purpose === 'attachment'
}

function normalizeMatrixMediaHttpUrl(
  fileUrl: string,
  purpose: AuthenticatedMediaPurpose,
): string {
  try {
    const client = getMatrixClient()
    if (purpose === 'avatar') {
      return toAvatarMatrixMediaUrl(client, fileUrl)
    }
    if (purpose === 'poster') {
      return toPosterMatrixMediaUrl(client, fileUrl)
    }
    return toAuthenticatedMatrixMediaUrl(client, fileUrl)
  } catch {
    // Client unavailable (session not ready) — fall back to plain URL rewrite.
    return isMatrixMediaHttpUrl(fileUrl) ? rewriteMatrixMediaHttpUrl(fileUrl) : fileUrl
  }
}

function isDirectDisplayUrl(src: string): boolean {
  // S3 / CDN / app static (`/daomed/...`) — no Matrix auth blob pipeline.
  return src.startsWith('blob:')
    || src.startsWith('data:')
    || (src.startsWith('http') && !isMatrixMediaHttpUrl(src))
    || (src.startsWith('/') && !isMatrixMediaHttpUrl(src))
}

function peekWarmBlob(src: string | undefined, purpose: AuthenticatedMediaPurpose): string {
  if (!src || isDirectDisplayUrl(src)) {
    return src && isDirectDisplayUrl(src) ? src : ''
  }
  return peekMatrixMediaBlobUrl(normalizeMatrixMediaHttpUrl(src, purpose)) || ''
}

export function useAuthenticatedMediaUrl(
  source: Ref<string | undefined> | string,
  options?: UseAuthenticatedMediaUrlOptions,
) {
  const purpose: AuthenticatedMediaPurpose = options?.purpose ?? 'avatar'

  // Warm start: S3 sync, Matrix blob from LRU if already parked — no empty first frame.
  const initial = unref(source)
  const resolvedSrc = ref(
    initial && isDirectDisplayUrl(initial) ? initial : peekWarmBlob(initial, purpose),
  )
  const isLoading = ref(false)
  const hasError = ref(false)
  let activeHttpUrl = ''
  let activeDirectSrc = initial && isDirectDisplayUrl(initial) ? initial : ''
  let loadToken = 0

  const releaseActiveBlob = () => {
    if (!activeHttpUrl) {
      return
    }

    releaseMatrixMediaBlobUrl(activeHttpUrl)
    activeHttpUrl = ''
  }

  const load = async (src?: string) => {
    const token = ++loadToken
    const startedAt = performance.now()

    if (!src) {
      // Avatars: keep last frame when parent briefly clears src (chat switch / groupRoom).
      // Attachments still clear so gallery/media don't show a stale frame.
      if (purpose === 'avatar' && resolvedSrc.value) {
        avatarDebugLog('ui · keep previous (empty src)', {
          kept: summarizeAvatarUrlForLog(resolvedSrc.value),
          activeHttpUrl: summarizeAvatarUrlForLog(activeHttpUrl),
        })
        hasError.value = false
        isLoading.value = false
        return
      }
      avatarDebugLog('ui · clear src', {
        hadResolved: Boolean(resolvedSrc.value),
        activeHttpUrl: summarizeAvatarUrlForLog(activeHttpUrl),
      })
      releaseActiveBlob()
      activeDirectSrc = ''
      resolvedSrc.value = ''
      hasError.value = false
      isLoading.value = false
      return
    }

    // Прямой URL (S3 и т.п.): не обнуляем картинку — иначе иконка «моргает» при каждом
    // новом signed URL того же файла или при повторном openChat.
    if (isDirectDisplayUrl(src)) {
      if (
        resolvedSrc.value
        && (resolvedSrc.value === src || isSameAvatarAsset(activeDirectSrc || resolvedSrc.value, src))
      ) {
        activeDirectSrc = activeDirectSrc || src
        hasError.value = false
        isLoading.value = false
        return
      }

      releaseActiveBlob()
      activeDirectSrc = src
      hasError.value = false

      // avatar for 0.5–2s while openChat blocked the main thread (visible blink).
      if (resolvedSrc.value && resolvedSrc.value !== src) {
        const previous = resolvedSrc.value
        const warm = findWarmDirectAvatar(src)
        const next = warm || src
        resolvedSrc.value = next
        isLoading.value = false
        rememberWarmDirectAvatar(next)
        avatarDebugLog('ui · direct switch (immediate)', {
          from: summarizeAvatarUrlForLog(previous),
          to: summarizeAvatarUrlForLog(next),
          warm: Boolean(warm),
          ms: Math.round(performance.now() - startedAt),
        })
        if (warm && warm !== src) {
          const img = new Image()
          img.onload = () => {
            rememberWarmDirectAvatar(src)
          }
          img.src = src
        }
        return
      }

      resolvedSrc.value = src
      isLoading.value = false
      rememberWarmDirectAvatar(src)
      return
    }

    activeDirectSrc = ''
    const httpUrl = normalizeMatrixMediaHttpUrl(src, purpose)

    // Тот же Matrix media — оставляем уже показанный blob.
    if (httpUrl === activeHttpUrl && resolvedSrc.value) {
      hasError.value = false
      isLoading.value = false
      return
    }

    // LRU hit — показать сразу, retain асинхронно.
    const peeked = peekMatrixMediaBlobUrl(httpUrl)
    if (peeked) {
      const previousHttpUrl = activeHttpUrl
      activeHttpUrl = httpUrl
      resolvedSrc.value = peeked
      hasError.value = false
      isLoading.value = false
      avatarDebugLog('ui · matrix warm peek', {
        httpUrl: summarizeAvatarUrlForLog(httpUrl),
        ms: Math.round(performance.now() - startedAt),
      })
      try {
        const blobUrl = await fetchMatrixMediaBlobUrl(httpUrl, {
          allowFullDownloadFallback: allowsFullDownloadFallback(purpose),
        })
        if (token !== loadToken) {
          releaseMatrixMediaBlobUrl(httpUrl)
          return
        }
        resolvedSrc.value = blobUrl
        if (previousHttpUrl && previousHttpUrl !== httpUrl) {
          releaseMatrixMediaBlobUrl(previousHttpUrl)
        }
      } catch {
        if (token === loadToken) {
          hasError.value = true
          avatarDebugLog('ui · matrix warm retain error', {
            httpUrl: summarizeAvatarUrlForLog(httpUrl),
            ms: Math.round(performance.now() - startedAt),
          })
        }
      }
      return
    }

    // Keep previous avatar visible while the new Matrix media downloads.
    const keptPrevious = Boolean(resolvedSrc.value)
    isLoading.value = !resolvedSrc.value
    hasError.value = false
    avatarDebugLog('ui · matrix fetch', {
      httpUrl: summarizeAvatarUrlForLog(httpUrl),
      purpose,
      keptPrevious,
      previous: summarizeAvatarUrlForLog(resolvedSrc.value),
    })

    try {
      const blobUrl = await fetchMatrixMediaBlobUrl(httpUrl, {
        allowFullDownloadFallback: allowsFullDownloadFallback(purpose),
      })

      if (token !== loadToken) {
        releaseMatrixMediaBlobUrl(httpUrl)
        avatarDebugLog('ui · matrix stale (token)', {
          httpUrl: summarizeAvatarUrlForLog(httpUrl),
          ms: Math.round(performance.now() - startedAt),
        })
        return
      }

      releaseActiveBlob()
      activeHttpUrl = httpUrl
      resolvedSrc.value = blobUrl
      const ms = Math.round(performance.now() - startedAt)
      avatarDebugLog(ms >= 800 ? 'ui · matrix slow' : 'ui · matrix ready', {
        httpUrl: summarizeAvatarUrlForLog(httpUrl),
        ms,
        keptPrevious,
      })
    } catch {
      if (token === loadToken) {
        hasError.value = true
        avatarDebugLog('ui · matrix error', {
          httpUrl: summarizeAvatarUrlForLog(httpUrl),
          purpose,
          ms: Math.round(performance.now() - startedAt),
        })
        if (!resolvedSrc.value) {
          releaseActiveBlob()
        }
      }
    } finally {
      if (token === loadToken) {
        isLoading.value = false
      }
    }
  }

  watch(
    () => unref(source),
    value => {
      void load(value).catch(() => {
        // load already sets hasError; swallow to avoid uncaught rejection noise
      })
    },
    { immediate: true },
  )

  onUnmounted(() => {
    loadToken += 1
    releaseActiveBlob()
  })

  const reload = async (options?: { force?: boolean }) => {
    if (options?.force) {
      const src = unref(source)
      if (src && !isDirectDisplayUrl(src)) {
        try {
          const httpUrl = normalizeMatrixMediaHttpUrl(src, purpose)
          invalidateMatrixMediaBlobUrl(httpUrl)
        } catch {
          // normalize may fail without client — load() still retries
        }
      }
      // Сброс, иначе load() вернёт мёртвый blob после idle/revoke.
      if (activeHttpUrl) {
        releaseMatrixMediaBlobUrl(activeHttpUrl)
        activeHttpUrl = ''
      }
      activeDirectSrc = ''
    }
    await load(unref(source))
  }

  return {
    resolvedSrc,
    isLoading,
    hasError,
    reload,
  }
}
